import { useEffect, useState, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import './Product.css';
import axiosInstance from '../../../Config/axiosInstance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Select from "react-select";
import { Snackbar, Typography, Alert} from '@mui/material';

const AddProduct = () => {
    const navigate = useNavigate();

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [filteredSubcategories, setFilteredSubcategories] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [dimensions, setDimensions] = useState([]);
    const [isTitleManuallyEdited, setIsManuallyEdited] = useState(false);
    const [autoGeneratedTitle, setAutoGeneratedTitle] = useState("");

    //optiunile pentru status
    const statusOptions = [
        {value: 'ACTIVE', label: 'Active'},
        {value: 'INACTIVE', label: 'Inactive'}
    ];

    const [formData, setFormData] = useState({
        title: "",
        description: "", 
        category: "", 
        subcategory: "",
        material: "",
        dimenssions: "",
        color: "",
        status: "ACTIVE"
    });

    //state-uri pentru validare si notificari
    const [titleError, setTitleError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "error"
    });

    //daca utilizatorul sters complet campul title, revenim la modul automat
    const handleTitleBlur = (e) => {
        if(e.target.value === " " && isTitleManuallyEdited) {
            setIsManuallyEdited(false)
        }
    }

    //functie pentru generarea titlului automat
    const generateAutoTitle = useCallback(async () => {
        //verifica daca avem cel putin un camp completat
        const hasData = formData.category || formData.subcategory || formData.material || formData.dimenssions || formData.color || formData.description;
        if(!hasData) {
            setAutoGeneratedTitle("");
            return;
        }

        try {
            const response = await axiosInstance.post("/product/generate-title-preview/", {
                category: formData.category ? parseInt(formData.category) : null,
                subcategory: formData.subcategory ? parseInt(formData.subcategory) : null,
                material: formData.material ? parseInt(formData.material) : null,
                dimenssions: formData.dimenssions ? parseInt(formData.dimenssions) : null,
                color: formData.color || null,
                description: formData.description || null
            });
            setAutoGeneratedTitle(response.data.title);
        } catch (error) {
            console.error("Error generating auto title: ", error);
            setAutoGeneratedTitle("Error generating title");
        }
    }, [formData.category, formData.subcategory, formData.material, formData.dimenssions, formData.color, formData.description]); 

    //functie pentru verificare existentei titlului la Submit
    const checkProductTitleExists = async(title) => {
        if(!title.trim()) {
            return false;
        }

        try {
            const response = await axiosInstance.get("/product/check-name/", {
                params: {name: title.trim()}
            });
            return response.data.exists;
        } catch (error) {
            console.error("Error checking product title: ", error);
            return false;
        }
    };

    //reseteaza eroarea atunci cand titlul se schimba
    useEffect(() => {
        if(titleError) {
            setTitleError("");
        }
    }, [formData.title, titleError]);

    useEffect(() => {
        //nu genera titlul automat daca utilizatorul a editat manual
        if(isTitleManuallyEdited) {
            return;
        }
        const timer = setTimeout(() => {
            generateAutoTitle();
        }, 500);

        return () => clearTimeout(timer);
    }, [
        formData.category,
        formData.subcategory,
        formData.material,
        formData.dimenssions,
        formData.color,
        formData.description, 
        isTitleManuallyEdited,
        generateAutoTitle
    ]);

    //actualizeaza campul title doar ddaca nu a fost editat manual
    useEffect(() => {
        if(!isTitleManuallyEdited && autoGeneratedTitle) {
            setFormData(prev => ({
                ...prev, 
                title: autoGeneratedTitle
            }));
        }
    }, [autoGeneratedTitle, isTitleManuallyEdited])

    const handleCloseSnackbar = () => {
        setSnackbar({...snackbar, open: false});
    }

    const handleChange = (e) => {
        const {name, value } = e.target;
        if(name === "title") {
            setIsManuallyEdited(true);
            setFormData(prev => ({
                ...prev,
                title: value,
            }));
        } else if (name === "category") {
            setFormData((prevState) => ({
                ...prevState,
                category: value,
                subcategory: "",
            }));

            const filtered = subcategories.filter(subcat => 
                subcat.category === parseInt(value)
            );
            setFilteredSubcategories(filtered);
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [name]: value,
            }));
        }
    };

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [catRes, subcatRes, matRes, dimRes] = await Promise.all([
                    axiosInstance.get('/category/'),
                    axiosInstance.get('/subcategory/'),
                    axiosInstance.get('/type-materials/'),
                    axiosInstance.get('/dimensions/'),
                ]);
                setCategories(catRes.data);
                setSubcategories(subcatRes.data);
                setMaterials(matRes.data);
                setDimensions(dimRes.data);
            } catch (error) {
                console.error("Error fetching options:", error);
            }
        };
        fetchOptions();
    }, []);

    useEffect(() => {
        if (formData.category) {
            const filtered = subcategories.filter(subcat => 
            subcat.category === parseInt(formData.category)
            );
            setFilteredSubcategories(filtered);
        } else {
            setFilteredSubcategories([]);
        }
        }, [formData.category, subcategories]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        //previne submitul multiplu
        if(isSubmitting) return;
        
        setIsSubmitting(true);
        
        //verifica daca titlul nu este gol
        if(!formData.title.trim()) {
            setTitleError("Title is required.");
            setSnackbar({
                open: true,
                message: "Title is required.",
                severity: "error"
            });
            setIsSubmitting(false);
            return;
        }

        //verifica daca titlul exista in sistem
        try {
            const exists = await checkProductTitleExists(formData.title);
            if(exists) {
                setTitleError("A product with this name already exists. Please choose a different title.");
                setSnackbar({
                    open: true,
                    message: "A product with this name already exists. Please choose a different title.",
                    severity: "error"
                });
                setIsSubmitting(false);
                return;
            }
        } catch (error) {
            setSnackbar({
                open: true, 
                message: "Error checking product existence. Please try again.",
                severity: "error"
            });
            setIsSubmitting(false);
            return;
        }

        //creaza produs
        try {
            const productResponse = await axiosInstance.post("/product/", formData);
            console.log("Created succesfully: ", productResponse.data);

            setSnackbar({
                open: true,
                message: "Product created successfully!",
                severity: "success"
            });

            setTimeout(() => {
                navigate(-1);
            }, 1500);
        } catch (error) {
            console.error("Error creating product: ", error.response?.data || error.message);
            
            //verifica daca titlul este duplicat 
            if(error.response?.status === 400) {
                const errorData = error.response.data;
                
                //verifica erorile
                if (typeof errorData === 'object') {
                    if (errorData.title && Array.isArray(errorData.title) && 
                        errorData.title[0]?.includes("already exists")) {
                        setTitleError("The name of this product already exists in the system.");
                        setSnackbar({
                            open: true,
                            message: "A product with this name already exists. Please choose different title.",
                            severity: "error"
                        });
                    } 
                    else if (errorData.non_field_errors && 
                            errorData.non_field_errors[0]?.includes("already exists")) {
                        setTitleError("The name of this product already exists in the system.");
                        setSnackbar({
                            open: true,
                            message: "A product with this name already exists. Please choose different title.",
                            severity: "error"
                        });
                    }
                    else if (errorData.detail?.includes("already exists")) {
                        setTitleError("The name of this product already exists in the system.");
                        setSnackbar({
                            open: true,
                            message: "A product with this name already exists. Please choose different title.",
                            severity: "error"
                        });
                    } else {
                        setSnackbar({
                            open: true,
                            message: "An error occurred. Please try again.",
                            severity: "error"
                        });
                    }
                } else {
                    setSnackbar({
                        open: true,
                        message: "An error occurred. Please try again.",
                        severity: "error"
                    });
                }
            } else {
                setSnackbar({
                    open: true,
                    message: "An error occurred. Please try again.",
                    severity: "error"
                });
            }
        }
    };

    //reseteaza isSubmitting cand titlul este modificat pentru a putea retrimite datele pentru cazul titlu unic
    useEffect(() => {
        if (isSubmitting && titleError) {
            setIsSubmitting(false);
        }
    }, [formData.title, titleError, isSubmitting]);

    return (
        <div className='product-page'>
        <div className='add-product-container'>
            <div className='product-header'>
                <ArrowBackIcon className='back-arrow' onClick={() => navigate(-1)} />
                <h2 className='add-product-title'>Create Product</h2>
            </div> 
        </div>

        <hr className='title-separator'/>
            
        <div className='add-product-container-form'>
            <form onSubmit={handleSubmit} className='add-material-form'>
                <div className='form-group'>
                    <label htmlFor='title' className='label'>
                        Title <span className='required-star'>*</span>
                        {formData.title && (
                            <span className='auto-badge'>
                                {isTitleManuallyEdited ? 'set manually' : 'auto-generated'}
                            </span>
                        )}
                    </label>
                    <input 
                        id="title"
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        onBlur={handleTitleBlur}
                        className={`input ${isTitleManuallyEdited ? 'manually-edited' : 'auto-generated'}`}
                    />
                    <div className='title-info'>                     
                        <Typography variant='caption' color='primary'>
                            The title is generated automatically as you select the fields below, or you can add a custom title.
                        </Typography>
                    </div>
                    {titleError && (
                        <Typography
                            variant='caption'
                            color='error'
                            sx={{mt: 1, display: "block"}}
                        >
                            {titleError}
                        </Typography>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="description" className="label">Description</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="textarea"
                    />
                </div>

                <div className='form-group'>
                    <label htmlFor='category' className='label'>Category</label>
                    <Select
                        classNamePrefix="react-select"
                        className="react-select"
                        options={categories.map(cat => ({
                            value: cat.id,
                            label: cat.title,
                        }))}
                        value={
                            categories
                                .map(cat => ({value: cat.id, label: cat.title}))
                                .find(opt => opt.value === formData.category) || null
                        }
                        onChange={(selectedOption) =>
                            setFormData(prev => ({
                                ...prev,
                                category: selectedOption ? selectedOption.value : "",
                            }))
                        }
                        placeholder="-- Select Category --"
                        isClearable
                    />
                </div>

                <div className='form-group'>
                    <label htmlFor='subcategory' className='label'>Subcategory</label>
                    <Select 
                        classNamePrefix="react-select"
                        className="react-select"
                        options={filteredSubcategories.map(subcat => ({
                            value: subcat.id,
                            label: subcat.title,
                        }))}
                        value={
                            filteredSubcategories
                            .map(subcat => ({value: subcat.id, label: subcat.title}))
                            .find(opt => opt.value === formData.subcategory) || null
                        }
                        onChange={(selectedOption) => 
                            setFormData(prev =>({
                                ...prev, 
                                subcategory: selectedOption ?  selectedOption.value : "",
                            }))
                        }
                        placeholder="-- Select Subcategory --"
                        isClearable
                    />
                </div>

                <div className="form-group-row">
                    <div className='form-group'>
                        <label htmlFor='dimenssions' className='label'>Dimension</label>
                        <Select 
                            classNamePrefix="react-select"
                            className="react-select"
                            options = {dimensions.map(dim => ({
                                value: dim.id,
                                label: dim.dimenssions,
                            }))}
                            onChange={(selectedOption) => 
                                setFormData(prev => ({
                                    ...prev,
                                    dimenssions: selectedOption ? selectedOption.value : "",
                                }))
                            }
                            placeholder="-- Select Dimension --"
                            isClearable
                        />
                    </div>

                    <div className='form-group'>
                        <label htmlFor='material' className='label'>Type of Materials</label>
                        <Select 
                            classNamePrefix="react-select"
                            className="react-select"
                            options = {materials.map(mat => ({
                                value: mat.id,
                                label: mat.name,
                            }))}
                            value = {
                               materials
                               .map(mat => ({value: mat.id, label: mat.name})) 
                               .find(opt => opt.value === formData.material) || null
                            }
                            onChange={(selectedOption) => 
                                setFormData(prev => ({
                                    ...prev,
                                    material: selectedOption ? selectedOption.value : "",
                                }))
                            }
                            placeholder="-- Select Material --"
                            isClearable
                        />
                    </div>
                </div>

                <div className='form-group'>
                    <label htmlFor='color' className='label'>Color</label>
                    <input 
                        id="color"
                        type="text"
                        name="color"
                        value={formData.color}
                        onChange={handleChange}
                        className='input'
                    />
                </div>

                <div className='form-group'>
                    <label htmlFor='status' className='label'>Status</label>
                    <Select
                        classNamePrefix="react-select"
                        className='react-select'
                        options={statusOptions}
                        value={statusOptions.find(opt => opt.value === formData.status) || statusOptions[0]}
                        onChange={(selectedOption) => 
                            setFormData(prev => ({
                                ...prev, 
                                status: selectedOption ? selectedOption.value: 'ACTIVE',
                            }))
                        }
                        placeholder = '--Select Status--'
                    />
                </div>

                <button 
                    type="submit" 
                    className='button-save'
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Saving..." : "Save" }
                </button>
            </form>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{vertical: "top", horizontal: "center"}}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
        </div>
    );
};

export default AddProduct;
